/**
 * Toolbox → Resources tab
 *
 * Per-modul a per-container resource limity (CPU, RAM, pids, tmpfs, IOPS,
 * network). Přehled kapacity nodu + alokace + live usage.
 *
 * Backend: tooly toolbox.resources_* + API /apiext/toolbox/resources/*
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { StatCard, StatsGrid } from '../../components/ui/Stats';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { Activity, Cpu, HardDrive, MemoryStick, Pencil } from 'lucide-react';

interface CapacityResp {
  ok: boolean;
  node: {
    cpu_cores: number;
    memory: {
      total_bytes: number;
      free_bytes: number;
      used_bytes: number;
      total_human: string;
      free_human: string;
    };
    disk: {
      total_bytes: number;
      free_bytes: number;
      used_bytes: number;
      total_human: string;
      free_human: string;
    };
  };
  allocated: {
    cpus: number;
    cpu_pct_of_node: number | null;
    memory_bytes: number;
    memory_pct_of_node: number | null;
    pids: number;
    memory_human: string;
  };
  live: { cpu_pct_sum: number; memory_bytes_sum: number; memory_human: string };
  per_module: Record<
    string,
    { cpus: number; memory_bytes: number; pids: number; containers: number }
  >;
  totals: { containers: number };
}

interface ResourceLimits {
  cpus?: string;
  memory?: string;
  memory_swap?: string;
  pids_limit?: number;
  tmpfs_size?: string;
  device_read_iops?: number;
  device_write_iops?: number;
  device_read_bps?: number;
  device_write_bps?: number;
  network_rate_bps?: number;
}

interface ListItem {
  id: string;
  module: string;
  label?: string;
  module_default: ResourceLimits;
  profile: { name: string; resources: ResourceLimits } | null;
  configured: ResourceLimits & { per_service?: Record<string, ResourceLimits> };
  effective: ResourceLimits;
  live: null | {
    cpu_pct: number;
    mem_usage_bytes: number;
    mem_limit_bytes: number;
    mem_pct: number;
    pids: number;
  };
}

const humanBytes = (n: number): string => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} K`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} M`;
  return `${(n / 1024 ** 3).toFixed(2)} G`;
};

function CapacitySection() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['tb-resources-capacity'],
    queryFn: () => api.mcp<CapacityResp>('toolbox.resources_capacity', {}),
    refetchInterval: 15_000,
  });
  if (isLoading) return <div className='text-gray-500 text-sm'>{t('common.loading')}</div>;
  if (!data?.ok) {
    return <div className='text-red-400 text-sm'>{t('toolboxResources.capacityLoadError')}</div>;
  }

  const n = data.node;
  const a = data.allocated;
  const l = data.live;
  const memPct = n.memory.total_bytes > 0
    ? Math.round(((n.memory.total_bytes - n.memory.free_bytes) / n.memory.total_bytes) * 100)
    : 0;
  const diskPct = n.disk.total_bytes > 0
    ? Math.round((n.disk.used_bytes / n.disk.total_bytes) * 100)
    : 0;

  return (
    <div className='space-y-4'>
      <StatsGrid>
        <StatCard
          label={t('toolboxResources.statCpuCores')}
          value={n.cpu_cores}
          icon={Cpu}
          sub={t('toolboxResources.statCpuSub', {
            allocated: a.cpus,
            pct: a.cpu_pct_of_node ?? 0,
            live: l.cpu_pct_sum,
          })}
        />
        <StatCard
          label={t('toolboxResources.statRam')}
          value={n.memory.total_human}
          icon={MemoryStick}
          sub={t('toolboxResources.statRamSub', {
            free: n.memory.free_human,
            pct: 100 - memPct,
            allocated: a.memory_human,
          })}
        />
        <StatCard
          label={t('toolboxResources.statDisk')}
          value={n.disk.total_human}
          icon={HardDrive}
          sub={t('toolboxResources.statDiskSub', { free: n.disk.free_human, pct: 100 - diskPct })}
        />
        <StatCard
          label={t('toolboxResources.statContainers')}
          value={data.totals.containers}
          icon={Activity}
          sub={t('toolboxResources.statContainersSub', { pids: a.pids })}
        />
      </StatsGrid>

      <Card>
        <CardHeader>
          <CardTitle>{t('toolboxResources.perModuleAllocationTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <table className='w-full text-sm'>
            <thead className='text-xs text-gray-500 border-b border-gray-800'>
              <tr>
                <th className='text-left py-2'>{t('toolboxResources.colModule')}</th>
                <th className='text-right'>{t('toolboxResources.colContainers')}</th>
                <th className='text-right'>{t('toolboxResources.colCpuCores')}</th>
                <th className='text-right'>{t('toolboxResources.colRam')}</th>
                <th className='text-right'>{t('toolboxResources.colPids')}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.per_module).sort(([, a], [, b]) =>
                b.memory_bytes - a.memory_bytes
              )
                .map(([mod, m]) => (
                  <tr key={mod} className='border-b border-gray-800/40'>
                    <td className='py-1.5 text-white'>{mod}</td>
                    <td className='text-right text-gray-400'>{m.containers}</td>
                    <td className='text-right text-gray-400'>{m.cpus || '—'}</td>
                    <td className='text-right text-gray-400'>{humanBytes(m.memory_bytes)}</td>
                    <td className='text-right text-gray-400'>{m.pids || '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function ContainersSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [edit, setEdit] = useState<ListItem | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['tb-resources-list'],
    queryFn: () =>
      api.mcp<{ ok: boolean; items: ListItem[] }>('toolbox.resources_list', { include_live: true }),
    refetchInterval: 30_000,
  });

  const { data: profilesData } = useQuery({
    queryKey: ['tb-resources-profiles'],
    queryFn: () =>
      api.mcp<{ profiles: Array<{ name: string; label: string }> }>('toolbox.resources_profiles_list', {}),
  });
  const profiles = profilesData?.profiles ?? [];

  const applyProfile = useMutation({
    mutationFn: ({ id, profile }: { id: string; profile: string | null }) =>
      api.mcp('toolbox.resources_profile_apply', { container_id: id, profile_name: profile }),
    onSuccess: () => {
      toast.success(t('toolboxResources.profileAssignedToast'));
      qc.invalidateQueries({ queryKey: ['tb-resources-list'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <div className='text-gray-500 text-sm'>{t('common.loading')}</div>;
  const items = data?.items ?? [];

  return (
    <div className='space-y-3'>
      <div className='text-xs text-gray-500'>
        {t('toolboxResources.hintEditPart1')}{' '}
        <code>docker update</code>{t('toolboxResources.hintEditPart2')}
      </div>
      <Card>
        <CardContent>
          <table className='w-full text-sm'>
            <thead className='text-xs text-gray-500 border-b border-gray-800'>
              <tr>
                <th className='text-left py-2'>{t('toolboxResources.colContainer')}</th>
                <th className='text-left'>{t('toolboxResources.colModule')}</th>
                <th className='text-left'>{t('toolboxResources.colProfile')}</th>
                <th className='text-right'>{t('toolboxResources.colCpuLimit')}</th>
                <th className='text-right'>{t('toolboxResources.colRamLimit')}</th>
                <th className='text-right'>{t('toolboxResources.colPids')}</th>
                <th className='text-right'>{t('toolboxResources.colLiveCpu')}</th>
                <th className='text-right'>{t('toolboxResources.colLiveRam')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const e = it.effective;
                const live = it.live;
                return (
                  <tr key={it.id} className='border-b border-gray-800/40 hover:bg-gray-900/40'>
                    <td className='py-1.5 text-white font-mono text-xs'>{it.id}</td>
                    <td className='text-gray-400'>{it.module}</td>
                    <td>
                      <Select
                        value={it.profile?.name ?? ''}
                        onChange={(ev) =>
                          applyProfile.mutate({
                            id: it.id,
                            profile: ev.target.value || null,
                          })}
                      >
                        <option value=''>{t('toolboxResources.noProfileOption')}</option>
                        {profiles.map((p) => (
                          <option key={p.name} value={p.name}>{p.label}</option>
                        ))}
                      </Select>
                    </td>
                    <td className='text-right text-gray-400'>{e.cpus ?? '—'}</td>
                    <td className='text-right text-gray-400'>{e.memory ?? '—'}</td>
                    <td className='text-right text-gray-400'>{e.pids_limit ?? '—'}</td>
                    <td className='text-right text-gray-400'>
                      {live ? live.cpu_pct.toFixed(1) : '—'}
                    </td>
                    <td className='text-right text-gray-400'>
                      {live ? humanBytes(live.mem_usage_bytes) : '—'}
                    </td>
                    <td>
                      <Button
                        variant='ghost'
                        size='icon'
                        icon={<Pencil size={12} />}
                        onClick={() => setEdit(it)}
                      />
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr>
                  <td colSpan={9} className='text-center text-gray-600 py-4'>
                    {t('toolboxResources.noContainersInRegistry')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {edit && (
        <EditModal
          item={edit}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            qc.invalidateQueries({ queryKey: ['tb-resources-list'] });
            qc.invalidateQueries({ queryKey: ['tb-resources-capacity'] });
          }}
        />
      )}
    </div>
  );
}

function EditModal(
  { item, onClose, onSaved }: { item: ListItem; onClose: () => void; onSaved: () => void },
) {
  const { t } = useTranslation();
  const [patch, setPatch] = useState<Record<string, string>>({
    cpus: item.configured.cpus ?? '',
    memory: item.configured.memory ?? '',
    memory_swap: item.configured.memory_swap ?? '',
    pids_limit: item.configured.pids_limit ? String(item.configured.pids_limit) : '',
    tmpfs_size: item.configured.tmpfs_size ?? '',
    device_read_iops: item.configured.device_read_iops
      ? String(item.configured.device_read_iops)
      : '',
    device_write_iops: item.configured.device_write_iops
      ? String(item.configured.device_write_iops)
      : '',
    network_rate_bps: item.configured.network_rate_bps
      ? String(item.configured.network_rate_bps)
      : '',
  });

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(patch)) {
        if (v === '') body[k] = null;
        else if (
          ['pids_limit', 'device_read_iops', 'device_write_iops', 'network_rate_bps'].includes(k)
        ) {
          body[k] = parseInt(v, 10);
        } else body[k] = v;
      }
      return api.mcp<{ ok: boolean; requires_recreate?: boolean }>(
        'toolbox.resources_set',
        { container_id: item.id, patch: body, apply_live: true },
      );
    },
    onSuccess: (r) => {
      toast.success(
        r.requires_recreate
          ? t('toolboxResources.savedRequiresRecreate')
          : t('toolboxResources.savedAppliedLive'),
      );
      onSaved();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const fld = (name: string, label: string, placeholder: string) => (
    <label className='block text-xs'>
      <span className='text-gray-400 mb-1 block'>{label}</span>
      <input
        className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm'
        value={patch[name] ?? ''}
        placeholder={placeholder}
        onChange={(e) => setPatch((p) => ({ ...p, [name]: e.target.value }))}
      />
    </label>
  );

  const modDef = item.module_default;
  const modDefSummary = Object.entries(modDef).map(([k, v]) => `${k}=${v}`).join(', ');

  return (
    <Modal open onClose={onClose} title={t('toolboxResources.limitsModalTitle', { id: item.id })}>
      <div className='space-y-3'>
        <div className='text-xs text-gray-500'>
          {t('toolboxResources.moduleDefaultLabel')}{' '}
          {modDefSummary || t('toolboxResources.noDefaultPlaceholder')}
        </div>
        <div className='grid grid-cols-2 gap-3'>
          {fld('cpus', t('toolboxResources.fieldCpuCores'), t('toolboxResources.placeholderCpuExample'))}
          {fld('memory', t('toolboxResources.fieldRam'), t('toolboxResources.placeholderMemoryExample'))}
          {fld('memory_swap', t('toolboxResources.fieldMemorySwap'), '2g')}
          {fld('pids_limit', t('toolboxResources.fieldPidLimit'), '512')}
          {fld('tmpfs_size', t('toolboxResources.fieldTmpfs'), '256m')}
          {fld('device_read_iops', t('toolboxResources.fieldReadIops'), '1000')}
          {fld('device_write_iops', t('toolboxResources.fieldWriteIops'), '1000')}
          {fld('network_rate_bps', t('toolboxResources.fieldNetRate'), '1048576')}
        </div>
        <div className='flex justify-end gap-2 pt-3'>
          <Button variant='secondary' onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant='primary' loading={save.isPending} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ModulesSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: modulesList } = useQuery({
    queryKey: ['tb-resources-modules-list'],
    queryFn: () =>
      api.mcp<{ ok: boolean; items: ListItem[] }>('toolbox.resources_list', {}),
  });
  const modules = Array.from(new Set((modulesList?.items ?? []).map((i) => i.module))).sort();
  return (
    <div className='space-y-3'>
      <div className='text-xs text-gray-500'>
        {t('toolboxResources.hintModuleDefaultsPart1')} <code>acm.module_config</code>{' '}
        {t('toolboxResources.hintModuleDefaultsPart2')}
      </div>
      {modules.map((mod) => (
        <ModuleDefaultsCard
          key={mod}
          module={mod}
          onSaved={() => qc.invalidateQueries({ queryKey: ['tb-resources-modules-list'] })}
        />
      ))}
      {modules.length === 0 && (
        <Card>
          <CardContent>
            <div className='text-gray-500 text-sm'>
              {t('toolboxResources.noModulesWithContainers')}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ModuleDefaultsCard(
  { module: mod, onSaved }: { module: string; onSaved: () => void },
) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, refetch } = useQuery({
    queryKey: ['tb-resources-module-defaults', mod],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        declared: ResourceLimits;
        override: ResourceLimits | null;
        effective: ResourceLimits;
        profile: { name: string; resources: ResourceLimits } | null;
      }>('toolbox.resources_module_defaults', { module: mod, action: 'get' }),
  });
  const { data: profilesData } = useQuery({
    queryKey: ['tb-resources-profiles'],
    queryFn: () => api.mcp<{ profiles: Array<{ name: string; label: string }> }>('toolbox.resources_profiles_list', {}),
  });
  const profiles = profilesData?.profiles ?? [];

  const setProfile = useMutation({
    mutationFn: (profile: string | null) =>
      api.mcp('toolbox.module_default_profile_set', { module: mod, profile_name: profile }),
    onSuccess: () => {
      toast.success(t('toolboxResources.profileAssignedToModuleToast'));
      refetch();
      qc.invalidateQueries({ queryKey: ['tb-resources-list'] });
      onSaved();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [open, setOpen] = useState(false);
  if (!data) return null;

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <CardTitle>{mod}</CardTitle>
          <div className='flex items-center gap-2'>
            <span className='text-[11px] text-gray-500'>{t('toolboxResources.profileLabel')}</span>
            <Select
              value={data.profile?.name ?? ''}
              onChange={(ev) => setProfile.mutate(ev.target.value || null)}
            >
              <option value=''>{t('toolboxResources.noProfileOption')}</option>
              {profiles.map((p) => <option key={p.name} value={p.name}>{p.label}</option>)}
            </Select>
            <Button variant='secondary' size='sm' onClick={() => setOpen(true)}>
              {t('toolboxResources.editOverrideButton')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className='grid grid-cols-4 gap-4 text-xs'>
          <div>
            <div className='text-gray-500 mb-1'>{t('toolboxResources.declaredLabel')}</div>
            <pre className='text-gray-400 whitespace-pre-wrap'>
{data.declared && Object.keys(data.declared).length > 0
  ? JSON.stringify(data.declared, null, 2) : '—'}
            </pre>
          </div>
          <div>
            <div className='text-gray-500 mb-1'>
              {t('toolboxResources.profileColumnLabel', { name: data.profile?.name ?? '—' })}
            </div>
            <pre className='text-gray-400 whitespace-pre-wrap'>
{data.profile?.resources && Object.keys(data.profile.resources).length > 0
  ? JSON.stringify(data.profile.resources, null, 2) : '—'}
            </pre>
          </div>
          <div>
            <div className='text-gray-500 mb-1'>{t('toolboxResources.overrideDbLabel')}</div>
            <pre className='text-gray-400 whitespace-pre-wrap'>
{data.override && Object.keys(data.override).length > 0
  ? JSON.stringify(data.override, null, 2) : '—'}
            </pre>
          </div>
          <div>
            <div className='text-gray-500 mb-1'>{t('toolboxResources.effectiveLabel')}</div>
            <pre className='text-white whitespace-pre-wrap'>
{data.effective && Object.keys(data.effective).length > 0
  ? JSON.stringify(data.effective, null, 2) : '—'}
            </pre>
          </div>
        </div>
      </CardContent>
      {open && (
        <ModuleDefaultEditModal
          module={mod}
          initial={data.override ?? {}}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            refetch();
            onSaved();
          }}
        />
      )}
    </Card>
  );
}

function ModuleDefaultEditModal({ module: mod, initial, onClose, onSaved }: {
  module: string;
  initial: ResourceLimits;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [patch, setPatch] = useState<Record<string, string>>({
    cpus: initial.cpus ?? '',
    memory: initial.memory ?? '',
    pids_limit: initial.pids_limit ? String(initial.pids_limit) : '',
    tmpfs_size: initial.tmpfs_size ?? '',
  });
  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(patch)) {
        if (v === '') body[k] = null;
        else if (k === 'pids_limit') body[k] = parseInt(v, 10);
        else body[k] = v;
      }
      return api.mcp('toolbox.resources_module_defaults', { module: mod, action: 'set', patch: body });
    },
    onSuccess: () => {
      toast.success(t('toolboxResources.savedToast'));
      onSaved();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const fld = (name: string, label: string, placeholder: string) => (
    <label className='block text-xs'>
      <span className='text-gray-400'>{label}</span>
      <input
        className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm mt-1'
        value={patch[name] ?? ''}
        placeholder={placeholder}
        onChange={(e) => setPatch((p) => ({ ...p, [name]: e.target.value }))}
      />
    </label>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={t('toolboxResources.defaultLimitsModalTitle', { module: mod })}
    >
      <div className='space-y-3'>
        {fld('cpus', t('toolboxResources.fieldCpuCores'), '0.5')}
        {fld('memory', t('toolboxResources.fieldRam'), '512m')}
        {fld('pids_limit', t('toolboxResources.fieldPidLimit'), '512')}
        {fld('tmpfs_size', t('toolboxResources.fieldTmpfs'), '256m')}
        <div className='flex justify-end gap-2 pt-2'>
          <Button variant='secondary' onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant='primary' loading={save.isPending} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function ResourcesTab() {
  const { t } = useTranslation();
  return (
    <div className='space-y-6'>
      <CapacitySection />
      <Card>
        <CardHeader>
          <CardTitle>{t('toolboxResources.profilesTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ProfilesSection />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{t('toolboxResources.containersTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ContainersSection />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{t('toolboxResources.moduleDefaultsTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ModulesSection />
        </CardContent>
      </Card>
    </div>
  );
}

// ── Profily section + CRUD ───────────────────────────────────────────────────

interface ResourceProfile {
  id: string;
  name: string;
  label: string;
  description: string | null;
  resources: ResourceLimits;
  builtin: boolean;
}

function ProfilesSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ResourceProfile | null>(null);
  const [creating, setCreating] = useState(false);

  const { data } = useQuery({
    queryKey: ['tb-resources-profiles'],
    queryFn: () =>
      api.mcp<{ ok: boolean; profiles: ResourceProfile[] }>('toolbox.resources_profiles_list', {}),
  });
  const profiles = data?.profiles ?? [];

  const remove = useMutation({
    mutationFn: (name: string) =>
      api.mcp<{ ok: boolean; error?: string }>('toolbox.resources_profiles_delete', { name }),
    onSuccess: (r, name) => {
      if (r.ok) {
        toast.success(t('toolboxResources.profileDeletedToast', { name }));
        qc.invalidateQueries({ queryKey: ['tb-resources-profiles'] });
      } else {
        toast.error(r.error ?? t('toolboxResources.deleteFailedToast'));
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between'>
        <div className='text-xs text-gray-500'>
          {t('toolboxResources.profilesHint')}
        </div>
        <Button variant='primary' size='sm' onClick={() => setCreating(true)}>
          {t('toolboxResources.newProfileButton')}
        </Button>
      </div>
      <table className='w-full text-sm'>
        <thead className='text-xs text-gray-500 border-b border-gray-800'>
          <tr>
            <th className='text-left py-2'>{t('toolboxResources.colSlug')}</th>
            <th className='text-left'>{t('toolboxResources.colLabel')}</th>
            <th className='text-left'>{t('toolboxResources.colCpu')}</th>
            <th className='text-left'>{t('toolboxResources.colRam')}</th>
            <th className='text-left'>{t('toolboxResources.colPids')}</th>
            <th className='text-left'>{t('toolboxResources.colOther')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {profiles.map((p) => (
            <tr key={p.name} className='border-b border-gray-800/40 hover:bg-gray-900/30'>
              <td className='py-1.5 font-mono text-white'>
                {p.name}
                {p.builtin && (
                  <Badge variant='info' className='ml-2 text-[10px]'>
                    {t('toolboxResources.builtinBadge')}
                  </Badge>
                )}
              </td>
              <td className='text-gray-400'>{p.label}</td>
              <td className='text-gray-400 font-mono text-xs'>{p.resources.cpus ?? '—'}</td>
              <td className='text-gray-400 font-mono text-xs'>{p.resources.memory ?? '—'}</td>
              <td className='text-gray-400 font-mono text-xs'>{p.resources.pids_limit ?? '—'}</td>
              <td className='text-gray-500 text-[11px]'>
                {[
                  p.resources.tmpfs_size && `tmpfs=${p.resources.tmpfs_size}`,
                  p.resources.device_read_iops && `r-iops=${p.resources.device_read_iops}`,
                  p.resources.device_write_iops && `w-iops=${p.resources.device_write_iops}`,
                  p.resources.network_rate_bps && `net=${p.resources.network_rate_bps}b/s`,
                ].filter(Boolean).join(' ') || '—'}
              </td>
              <td className='text-right'>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() =>
                    setEditing(p)}
                >
                  {t('toolboxResources.editButton')}
                </Button>
                {!p.builtin && (
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => remove.mutate(p.name)}
                  >
                    {t('toolboxResources.deleteButton')}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {(editing || creating) && (
        <ProfileEditModal
          initial={editing ?? null}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['tb-resources-profiles'] });
          }}
        />
      )}
    </div>
  );
}

function ProfileEditModal({ initial, onClose, onSaved }: {
  initial: ResourceProfile | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isCreate = !initial;
  const [name, setName] = useState(initial?.name ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [res, setRes] = useState<Record<string, string>>({
    cpus: initial?.resources.cpus ?? '',
    memory: initial?.resources.memory ?? '',
    pids_limit: initial?.resources.pids_limit ? String(initial.resources.pids_limit) : '',
    tmpfs_size: initial?.resources.tmpfs_size ?? '',
    device_read_iops: initial?.resources.device_read_iops
      ? String(initial.resources.device_read_iops)
      : '',
    device_write_iops: initial?.resources.device_write_iops
      ? String(initial.resources.device_write_iops)
      : '',
    network_rate_bps: initial?.resources.network_rate_bps
      ? String(initial.resources.network_rate_bps)
      : '',
  });

  const save = useMutation({
    mutationFn: () => {
      const resources: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(res)) {
        if (!v) continue;
        if (
          ['pids_limit', 'device_read_iops', 'device_write_iops', 'network_rate_bps'].includes(k)
        ) {
          resources[k] = parseInt(v, 10);
        } else resources[k] = v;
      }
      const body = { name, label, description: description || null, resources };
      return isCreate
        ? api.mcp('toolbox.resources_profiles_create', body)
        : api.mcp('toolbox.resources_profiles_update', {
          name: initial!.name,
          label,
          description: description || null,
          resources,
        });
    },
    onSuccess: () => {
      toast.success(
        isCreate
          ? t('toolboxResources.profileCreatedToast')
          : t('toolboxResources.profileUpdatedToast'),
      );
      onSaved();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const fld = (k: string, l: string, ph: string) => (
    <label className='block text-xs'>
      <span className='text-gray-400 mb-1 block'>{l}</span>
      <input
        className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm'
        value={res[k] ?? ''}
        placeholder={ph}
        onChange={(e) => setRes((p) => ({ ...p, [k]: e.target.value }))}
      />
    </label>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={isCreate
        ? t('toolboxResources.newProfileTitle')
        : t('toolboxResources.profileModalTitle', { name: initial!.name })}
    >
      <div className='space-y-3'>
        {isCreate && (
          <label className='block text-xs'>
            <span className='text-gray-400 mb-1 block'>{t('toolboxResources.fieldSlug')}</span>
            <input
              className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm font-mono'
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='my-profile'
            />
          </label>
        )}
        <label className='block text-xs'>
          <span className='text-gray-400 mb-1 block'>{t('toolboxResources.fieldLabel')}</span>
          <input
            className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm'
            value={label}
            onChange={(e) =>
              setLabel(e.target.value)}
            placeholder={t('toolboxResources.placeholderProfileLabel')}
          />
        </label>
        <label className='block text-xs'>
          <span className='text-gray-400 mb-1 block'>
            {t('toolboxResources.fieldDescription')}
          </span>
          <input
            className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm'
            value={description}
            onChange={(e) =>
              setDescription(e.target.value)}
          />
        </label>
        <div className='grid grid-cols-2 gap-3'>
          {fld('cpus', t('toolboxResources.fieldCpuCores'), '0.5')}
          {fld('memory', t('toolboxResources.fieldRam'), '512m')}
          {fld('pids_limit', t('toolboxResources.fieldPidLimit'), '512')}
          {fld('tmpfs_size', t('toolboxResources.fieldTmpfs'), '256m')}
          {fld('device_read_iops', t('toolboxResources.fieldReadIops'), '1000')}
          {fld('device_write_iops', t('toolboxResources.fieldWriteIops'), '1000')}
          {fld('network_rate_bps', t('toolboxResources.fieldNetRate'), '1048576')}
        </div>
        <div className='flex justify-end gap-2 pt-3'>
          <Button variant='secondary' onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant='primary'
            loading={save.isPending}
            onClick={() =>
              save.mutate()}
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
